import { useEffect, useState } from 'react';
import { Alert, Center, Container, Loader, Modal, Text } from '@mantine/core';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { buildSessionExpiryStorageKey, isSessionLocallyValid } from '../api/auth';
import GenericForm from '../components/GenericForm';
import NotFoundPage from './NotFoundPage';
import RequestLinkForm from '../components/RequestLinkForm';
import type { SiteConfig, SiteType } from '../types/schema';
import { toSiteConfig } from '../types/schema';
import { saveReturnTo } from '../hooks/returnTo';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { buildReturnToPath } from '../utils/portalPath';
import { resolveEnrollmentProduct } from '../utils/enrollmentProduct';

interface FormPageProps {
  siteType: SiteType;
}

export default function FormPage({ siteType }: FormPageProps) {
  const { data: config, isLoading, error, sessionScopeKey } = useResolvedProgram();
  const { product } = useParams<{ product: string }>();
  const location = useLocation();
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionQuery = useProtectedSession(sessionScopeKey);

  useEffect(() => {
    if (!sessionScopeKey) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (!isSessionLocallyValid(sessionScopeKey)) {
        setSessionExpired(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sessionScopeKey]);

  useEffect(() => {
    if (sessionExpired) {
      saveReturnTo(buildReturnToPath(location));
    }
  }, [location, sessionExpired]);

  useEffect(() => {
    if (!sessionExpired || !sessionScopeKey) {
      return undefined;
    }

    const storageKey = buildSessionExpiryStorageKey(sessionScopeKey);

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage || event.key !== storageKey || !event.newValue) {
        return;
      }

      if (!isSessionLocallyValid(sessionScopeKey)) {
        return;
      }

      void sessionQuery.refetch().then((result) => {
        if (!result.error && result.data?.authenticated) {
          setSessionExpired(false);
        }
      });
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [sessionExpired, sessionQuery, sessionScopeKey]);

  if (isLoading || sessionQuery.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !config) {
    return <NotFoundPage />;
  }

  const productConfig = siteType === 'enrollments' ? resolveEnrollmentProduct(config, product) : null;
  if (product && !productConfig) {
    return <NotFoundPage />;
  }
  const siteConfig: SiteConfig | null = productConfig
    ? { ...productConfig.effectiveConfig, siteType, pages: productConfig.pages }
    : toSiteConfig(config, siteType);
  if (!siteConfig) {
    return <NotFoundPage />;
  }

  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: buildReturnToPath(location) }} replace />;
  }

  if (sessionQuery.isError) {
    return (
      <Container size="sm" pt="xl">
        <Alert color="red" title="Session Error">
          Unable to verify your session. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Modal
        opened={sessionExpired}
        onClose={() => {}}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        centered
        title="Session expired"
      >
        <Text size="sm" c="dimmed" mb="md">
          Your session expired while this tab was inactive. Request a new sign-in link to continue without losing the form data in this tab.
        </Text>
        <RequestLinkForm
          description="Enter your authorized work email address and we’ll send you a fresh sign-in link."
          submitLabel="Send a new sign-in link"
          successMessage="Check your email for a fresh sign-in link. After you complete sign-in, this form will be available again."
        />
      </Modal>
      <GenericForm key={product ?? siteType} config={siteConfig} sessionEmail={sessionQuery.data?.email} product={product} />
    </>
  );
}
